/**
 * "The newest reading" picked by timestamp, written once for the live-data checklist and the
 * fleet check.
 *
 * By timestamp rather than by array position: readings are paged and merged concurrently, so the
 * order they arrive in says nothing about which is newest. A reading whose timestamp cannot be
 * read never displaces one whose timestamp can.
 */

export interface NewestEntry<T> {
  item: T;
  ms: number;
}

/**
 * Milliseconds of a timestamp, or NaN when unreadable.
 *
 * `dateTime` is typed as a `Date`, but not every caller hands one over (a string survives
 * JSON), so a non-Date is parsed rather than assumed.
 */
export function readingMs(dateTime: Date | string): number {
  return dateTime instanceof Date ? dateTime.getTime() : new Date(dateTime).getTime();
}

/** Folds `items` into `newest`, keeping the newest item per key. Callable once per page. */
export function keepNewestPerKey<T>(
  newest: Map<string, NewestEntry<T>>,
  items: Iterable<T>,
  keyOf: (item: T) => string,
  timeOf: (item: T) => Date | string,
): void {
  for (const item of items) {
    const ms = readingMs(timeOf(item));
    if (!Number.isFinite(ms)) {
      continue;
    }

    const key = keyOf(item);
    const previous = newest.get(key);
    if (previous === undefined || ms > previous.ms) {
      newest.set(key, { item, ms });
    }
  }
}

/** The newest item per key. */
export function newestPerKey<T>(
  items: Iterable<T>,
  keyOf: (item: T) => string,
  timeOf: (item: T) => Date | string,
): T[] {
  const newest = new Map<string, NewestEntry<T>>();
  keepNewestPerKey(newest, items, keyOf, timeOf);
  return [...newest.values()].map(entry => entry.item);
}

/** The newest of `items`, or `undefined` when none has a readable timestamp. */
export function newestOf<T>(items: Iterable<T>, timeOf: (item: T) => Date | string): T | undefined {
  let newest: NewestEntry<T> | undefined;

  for (const item of items) {
    const ms = readingMs(timeOf(item));
    if (Number.isFinite(ms) && (newest === undefined || ms > newest.ms)) {
      newest = { item, ms };
    }
  }

  return newest?.item;
}
